import type { NuxtApp } from '#app';

const FALLBACK_TTL_MINUTES = 30;

/**
 * A function to be used as the `getCachedData` useAsyncData option
 *
 * This is only relevant for client side navigation
 *
 * For the key of the useAsyncData, retrieve the cached data from the server
 * if it exists. Otherwise return undefined, which will refetch the data.
 *
 * If the `addFetchedAtTransform` transform function is also used, this
 * function will check for a fallback expiration date for the cached data
 * so it doesn't become too old.
 */
export const getCachedDataFromServer = (key: string, nuxtApp: NuxtApp) => {
  const data = nuxtApp.payload.data[key];
  if (!data) {
    return;
  }

  if (data.fetchedAt) {
    const expirationDate = new Date(data.fetchedAt);
    expirationDate.setTime(
      expirationDate.getTime() + FALLBACK_TTL_MINUTES * 60 * 1000,
    );

    const isExpired = expirationDate.getTime() < Date.now();
    if (isExpired) {
      return;
    }
  }

  return data;
};

/**
 * A transform function to be used as the `transform` useAsyncData option
 *
 * This function adds a `fetchedAt` field to the data fetched with `useAsyncData`
 *
 * Only works if the data fetched from the backend is an object.
 */
export const addFetchedAtTransform = <T extends object>(
  input: T,
): T & { fetchedAt: Date } => {
  return {
    ...input,
    fetchedAt: new Date(),
  };
};
